import * as React from "react";
import { Autocomplete, AutocompleteProps } from "./Autocomplete";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { debounce } from "lodash-es";
import classNames from "classnames";

export type ApiAutocompleteProps<API_TYPE, VAL_TYPE> = {
    onSearchItems: (text: string) => CancellableApiPromise<API_TYPE[]>;
    onClearItems?: () => void;
    minSearchString?: number;
    debounceDelay?: number;
    initialItems?: API_TYPE[];
    /**
     * Pokud je zapnuto a není vybraný item, vrátí po stisknutí entru napsaný text. Nevolá se, pokud je povoleno allowCreateItems nebo tags.
     */
    onSearchByText?: (val:string) => void;
} & Omit<AutocompleteProps<API_TYPE, VAL_TYPE>, "items">;

export interface CancellableApiPromise<T> extends Promise<T> {
    cancel?: (reason?: string) => CancellableApiPromise<T>;
}

/**
 * Wrapper pro vytvoření cancellable api promise z předaného promise - pro možnost použití v api a jeho cancel.
 * @param promise promise
 * @param onCancel cancel callback
 */
export function cancellableApi<T>(promise: Promise<T>, onCancel?: () => void): CancellableApiPromise<T> {
    let cancel: ((reason: string) => CancellableApiPromise<T>) | null = null;
    let cancellable: CancellableApiPromise<T>;
    cancellable = new Promise((resolve, reject) => {
        cancel = () => {
            try {
                if (onCancel) {
                    onCancel();
                }
            } catch (e) {
                reject(e);
            }
            return cancellable;
        };
        promise.then(resolve, reject);
    });
    if (cancel) {
        cancellable.cancel = cancel;
    }
    return cancellable;
}

/**
 * API autocomplete.
 * Zatím nepodporuje debounce.
 */

export function ApiAutocomplete<API_TYPE, VAL_TYPE = API_TYPE>({
    minSearchString = 1,
    initialItems = [],
    debounceDelay = 500,
    noItemsFound,
    onSearchItems,
    onTextChange,
    onClearItems,
    className,
    ...other
}: ApiAutocompleteProps<API_TYPE, VAL_TYPE>) {
    const [items, setItems] = React.useState(initialItems);
    const [searchText, setText] = React.useState("");
    const [searching, setSearching] = React.useState<boolean>(false);

    const currentApiSearch = React.useRef<CancellableApiPromise<API_TYPE[]>>(null);
    const callSearch = React.useRef<boolean>(false);
    const currentSearchText = React.useRef<String>("");

    const search = React.useCallback(
        debounce(text => {
            if (callSearch.current) {
                setSearching(true);

                if (currentApiSearch.current && currentApiSearch.current.cancel) {
                    currentApiSearch.current.cancel();
                }

                let call = onSearchItems(text);
                currentApiSearch.current = call;

                call.then(items => {
                    if (currentApiSearch.current === call && currentSearchText.current == text) {
                        currentApiSearch.current = null;
                        setItems(items);
                    }
                }).finally(() => {
                    setSearching(false);
                });
            }
        }, debounceDelay),
        [debounceDelay, onSearchItems],
    );

    const useMinSearchString = minSearchString || 1;

    let noItemsFoundText: string;
    if (searching) {
        noItemsFoundText = "Probíhá vyhledávání...";
    } else {
        if (searchText && searchText.length >= useMinSearchString) {
            noItemsFoundText = "Pro zadaný řetězec nebyly nalezeny žádné záznamy";
        } else {
            if (useMinSearchString == 1) {
                noItemsFoundText = "Pro vyhledání zadejte text (alespoň jeden znak)";
            } else if (useMinSearchString <= 3) {
                noItemsFoundText = `Pro vyhledání zadejte text (alespoň ${useMinSearchString} znaky)`;
            } else {
                noItemsFoundText = `Pro vyhledání zadejte text (alespoň ${useMinSearchString} znaků)`;
            }
        }
    }

    return (
        <Autocomplete<API_TYPE, VAL_TYPE>
            className={classNames(className, "api-autocomplete", {
                ["searching"]: searching,
            })}
            noItemsFound={noItemsFoundText}
            shouldItemRender={(_, __) => true}
            onCloseMenu={() => {
                if (other.value === null || other.value === undefined) {
                    setText("");
                    setItems([]);
                }
            }}
            showMenuOnFocus={false}
            dropDownIconClickOpenMenu={false}
            getDropDownIcon={() => faSearch}
            {...other}
            items={searching ? [] : items}
            onTextChange={text => {
                setText(text);
                currentSearchText.current = text;
                if (useMinSearchString <= text.length) {
                    setSearching(true);

                    setItems([]);
                    onClearItems && onClearItems();

                    callSearch.current = true;
                    search(text);
                } else {
                    if (currentApiSearch.current && currentApiSearch.current.cancel) {
                        currentApiSearch.current.cancel();
                    }
                    currentApiSearch.current = null;
                    callSearch.current = false;
                    if (searching) {
                        setSearching(false);
                    }

                    if (items && items.length > 0) {
                        setItems([]);
                        onClearItems && onClearItems();
                    }
                }
            }}
        />
    );
}
